import Vue from 'vue'
import {entryDetailsForIndex, rowsDiffer, showValue} from '@/utils/entryGrouping'

// Per-show class lists (SHO-405). A show's class list is shows.show_class_list_id. Shows can
// share a list, and a show's entry type still carries a default list (event_show_types.class_list_id)
// that new shows start on.
//
// Class rows reach the frontend two ways (show_home / admin payload):
//   event.show_types[]        each entry type with its class_list_id and class_list rows
//   event.extra_class_lists[] {class_list_id, class_list} for lists a show uses that no entry type
//                             of the event points to (normally empty)
// These helpers resolve "which list / which rows does this show use" in one place, instead of
// going show -> entry type -> class_list.

// The list id a show uses. Falls back to its entry type's list for show objects that don't carry
// show_class_list_id yet (e.g. a show added in this session before the payload is reloaded).
export function classListIdForShow (show, event) {
  if (!show) return null
  if (show.show_class_list_id) return show.show_class_list_id
  const type = entryTypeForShow(show, event)
  return type ? type.class_list_id || null : null
}

// The class rows for a list id, from whichever part of the payload carries them. An entry type
// whose rows are already loaded wins over one with an empty/missing class_list (the class list
// editor lazily fills show_types[].class_list).
export function classRowsForList (listId, event) {
  if (!listId || !event) return []
  const types = event.show_types || []
  const withRows = types.find(t => t.class_list_id === listId && Array.isArray(t.class_list) && t.class_list.length)
  if (withRows) return withRows.class_list
  const extra = (event.extra_class_lists || []).find(x => x.class_list_id === listId)
  if (extra && Array.isArray(extra.class_list)) return extra.class_list
  const any = types.find(t => t.class_list_id === listId && Array.isArray(t.class_list))
  return any ? any.class_list : []
}

// Put a list's rows where classRowsForList looks: on every entry type pointing at the list, else
// on event.extra_class_lists. Uses Vue.set so pages already rendering the event update.
export function cacheClassListRows (event, listId, rows) {
  if (!listId || !event) return
  let carried = false
  ;(event.show_types || []).forEach(st => {
    if (st.class_list_id === listId) {
      Vue.set(st, 'class_list', rows)
      carried = true
    }
  })
  if (!event.extra_class_lists) Vue.set(event, 'extra_class_lists', [])
  const extra = event.extra_class_lists.find(x => x.class_list_id === listId)
  if (extra) {
    Vue.set(extra, 'class_list', rows)
  } else if (!carried) {
    event.extra_class_lists.push({class_list_id: listId, class_list: rows})
  }
}

// The hierarchy fields (outer -> inner) from the chains stamped on class rows: [{field, index}].
// The entry form asks for these (NewEntry.hierarchyLevels) and check-in cards send them to it.
export function hierarchyLevelsFromRows (rows) {
  const levels = []
  ;(rows || []).forEach(c => {
    const hf = c && c.settings && c.settings.hierarchy_fields
    ;(hf || []).forEach((item, idx) => {
      if (!item || !item.field) return
      if (!levels.find(l => l.field === item.field)) levels.push({field: item.field, index: idx})
    })
  })
  return levels.sort((a, b) => a.index - b.index)
}

// A hierarchy field's label on the entry form (and check-in cards): breed_level -> Breed Level
export function hierarchyFieldLabel (field) {
  if (field === 'breed') {
    // The show's breed divisions may not be true breeds -- they're what the show
    // represents as the breed for their event.
    return 'Breed Division'
  }
  return field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

// The class rows the show uses.
export function classListForShow (show, event) {
  return classRowsForList(classListIdForShow(show, event), event)
}

// The show's entry type (event.show_types entry): by entry_details_id, else by show_ids.
export function entryTypeForShow (show, event) {
  if (!show || !event) return null
  const types = event.show_types || []
  return types.find(t => show.entry_details_id && t.event_show_type_id === show.entry_details_id) ||
    types.find(t => Array.isArray(t.show_ids) && t.show_ids.includes(show.show_id)) ||
    null
}

// The shows on a list (e.g. the class list editor's "used by").
export function showsOnClassList (listId, event) {
  if (!listId || !event) return []
  return (event.shows || []).filter(s => classListIdForShow(s, event) === listId)
}

// Moving a show to another entry type asks whether to keep its class list (Josh, 2026-09-28) --
// only when the entry type actually changed and the new type's default list differs from the list
// the show had.
export function classListChoiceNeeded ({originalEntryTypeId, entryTypeId, originalClassListId, newEntryTypeListId}) {
  if (!originalEntryTypeId || entryTypeId === originalEntryTypeId) return false
  if (!newEntryTypeListId) return false
  return newEntryTypeListId !== originalClassListId
}

// SHO-409: the entry form's selected shows grouped by class list, in selection order.
// selectedShows are the form's picks ({value: show_id, name} or show objects); they're resolved to
// event.shows by id so each show's list is known. The first group (the first selected show's list)
// is the "primary" one the form's class selector already drives; any others need their own class.
export function groupShowsByClassList (selectedShows, event) {
  const byId = {}
  ;((event && event.shows) || []).forEach(s => { byId[s.show_id] = s })
  const groups = []
  ;(selectedShows || []).forEach(sel => {
    const id = sel && (sel.value || sel.show_id)
    if (!id) return
    const show = byId[id] || {show_id: id, name: sel.name}
    const listId = classListIdForShow(show, event)
    let group = groups.find(g => g.listId === listId)
    if (!group) {
      group = {listId, shows: []}
      groups.push(group)
    }
    group.shows.push(show)
  })
  return groups
}

// SHO-409: the class to auto-select for a class list: the only candidate, else the only class on the
// list, else a previous choice that is still a candidate, else nothing (the user picks).
export function pickClassId (candidates, listRows, previousClassId) {
  const cands = candidates || []
  if (cands.length === 1) return cands[0].classID
  const rows = listRows || []
  if (rows.length === 1) return rows[0].classID
  if (previousClassId && cands.some(c => c.classID === previousClassId)) return previousClassId
  return null
}

// SHO-439: on an edit, the class a card's entries already have in these shows (the first one
// found), so a class list after the first starts from it like the first list does. null for a new
// entry or shows the card didn't have.
export function loadedClassIdForShows (card, shows) {
  const original = (card && card.originalShows) || []
  for (const sh of shows || []) {
    const id = sh && (sh.show_id || sh.value)
    const index = original.findIndex(o => (o && typeof o === 'object' ? (o.show_id || o.value) : o) === id)
    const classId = index === -1 ? null : showValue(card, index, 'class_id')
    if (classId) return classId
  }
  return null
}

// SHO-409: the class and entry details one existing entry of a card gets on an edit. A card's
// entry_ids line up with its originalShows (get_entry_group aggregates both in one pass); when the
// shows span class lists, each entry takes its own show's class (class_ids_by_show) and division
// (division_by_show) -- same rules as the backend's addEntries -- instead of the first list's.
// SHO-439: the details start from that entry's own (entryDetailsForIndex), so fields the user
// didn't change keep each show's own value.
export function entryEditForShow (updateEntry, index) {
  const original = ((updateEntry && updateEntry.originalShows) || [])[index]
  const showId = original && typeof original === 'object' ? (original.show_id || original.value) : original
  const details = (updateEntry && entryDetailsForIndex(updateEntry, index, updateEntry.entry_details)) || {}
  const classIds = (updateEntry && updateEntry.class_ids_by_show) || {}
  const divisions = (updateEntry && updateEntry.division_by_show) || {}
  const division = showId ? divisions[showId] : undefined
  let classId = (showId && classIds[showId]) || (updateEntry ? updateEntry.class_id : null)
  // SHO-439: a merged card's shows can sit in different classes; a class the user left as it was
  // loaded keeps each show's own
  if (updateEntry && updateEntry.loaded_class_id && classId === updateEntry.loaded_class_id) {
    classId = showValue(updateEntry, index, 'class_id') || classId
  }
  return {
    class_id: classId,
    entry_details: division && division !== details.division ? {...details, division} : details
  }
}

// SHO-439: the class row of the card's entry in one show, on that show's own list (a card's shows
// can be on different lists). null when the show or the class isn't found.
export function classForShow (card, index, show, event) {
  if (!card || !show) return null
  const classId = showValue(card, index, 'class_id')
  return classListForShow(show, event).find(c => c.classID === classId) || null
}

// What makes two classes the same class for display. Copying a list (ABGA to JABGA, copy from
// event) regenerates classIDs, so the same class on two lists only matches by these. Trimmed;
// blank and missing are equal. Legacy lists name the sub-division subDivision.
const SAME_CLASS_FIELDS = [
  c => c.division_group,
  c => c.division,
  c => c.sub_division || c.subDivision || (c.settings || {}).sub_division,
  c => c.className,
  c => c.entryType
]

export function classMatchKey (cls) {
  if (!cls) return null
  return JSON.stringify(SAME_CLASS_FIELDS.map(f => {
    const v = f(cls)
    return v === undefined || v === null ? '' : String(v).trim()
  }))
}

// Whether the card should name a class per show: its shows are in different classes, and those
// classes don't all match by classMatchKey. A class missing from its show's list never matches.
// Display only: edits still compare class ids (rowsDiffer).
export function classesDifferForDisplay (card, showsById, event) {
  if (!rowsDiffer(card, 'class_id')) return false
  const keys = (card.shows || []).map((showId, i) => classMatchKey(classForShow(card, i, (showsById || {})[showId], event)))
  return keys.some(k => k === null) || new Set(keys).size > 1
}

// SHO-439: the break type ('weight', ...) that decides whether a show asks for a weight. A card's
// shows can be in classes from different lists, and only some may break by weight.
//   with an index: that show's own class decides (the card's class when it isn't on the show's list)
//   without: 'weight' when any show's class breaks by weight, else the card's class decides
// The card's class is card.class_id on the first show's list, which is all this used to read; when
// the shows agree, both answers are exactly that.
export function classBreakType (card, index, showsById, event) {
  if (!card) return null
  const shows = card.shows || []
  const byId = showsById || {}
  const cardClass = classListForShow(byId[shows[0]], event).find(c => c.classID === card.class_id)
  const cardType = cardClass ? cardClass.break_type : null
  const typeForShow = i => {
    const c = classForShow(card, i, byId[shows[i]], event)
    return c ? c.break_type : cardType
  }
  if (index !== undefined && index !== null) return typeForShow(index)
  return shows.some((s, i) => typeForShow(i) === 'weight') ? 'weight' : cardType
}
